import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef, useId } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary'
  size?: 'default' | 'small' | 'icon'
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'default', className = '', type = 'button', ...rest },
  ref,
) {
  const classes = [
    'button',
    `button--${variant}`,
    size === 'small' ? 'button--small' : '',
    size === 'icon' ? 'button--small button--icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  return <button ref={ref} type={type} className={classes} {...rest} />
})

/** One field's validation message. Renders nothing when the field is valid. */
export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null
  return (
    <p className="field__error" id={id}>
      {message}
    </p>
  )
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string
  value: string
  onValueChange: (value: string) => void
  /** Shown beneath the field, and announced, when the value cannot be searched. */
  error?: string
}

export function TextField({ label, value, onValueChange, error, ...rest }: TextFieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`field__control${error ? ' field__control--invalid' : ''}`}
        type="text"
        autoComplete="off"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onValueChange(event.target.value)}
        {...rest}
      />
      <FieldError id={errorId} message={error} />
    </div>
  )
}

interface SelectFieldProps {
  label: string
  value: string
  options: readonly string[]
  onValueChange: (value: string) => void
}

export function SelectField({ label, value, options, onValueChange }: SelectFieldProps) {
  const id = useId()
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field__control"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  )
}

interface CheckboxProps {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

export function Checkbox({ label, checked, onCheckedChange }: CheckboxProps) {
  return (
    <label className="checkbox">
      <input
        className="checkbox__input"
        type="checkbox"
        checked={checked}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  )
}

export function Spinner() {
  return <span className="spinner" aria-hidden="true" />
}

interface StatusRowProps {
  children: ReactNode
  /** Offered while a search is running, so a slow one can be abandoned. */
  onCancel?: () => void
  cancelLabel?: string
}

export function StatusRow({ children, onCancel, cancelLabel = 'Cancel' }: StatusRowProps) {
  return (
    <p className="status-row" role="status">
      <Spinner />
      <span>{children}</span>
      {onCancel ? (
        <Button variant="secondary" size="small" onClick={onCancel}>
          {cancelLabel}
        </Button>
      ) : null}
    </p>
  )
}

interface CalloutProps {
  children: ReactNode
  onRetry?: () => void
  retryLabel?: string
}

export function Callout({ children, onRetry, retryLabel = 'Try again' }: CalloutProps) {
  return (
    <div className="callout" role="alert">
      <span>{children}</span>
      {onRetry ? (
        <Button variant="secondary" size="small" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  )
}
